'use client';

import React, { useCallback, useEffect, useRef } from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  Portal,
  useBreakpoint
} from 'components/pcc-components';
import {
  CatButton,
  CatHeading,
  CatIconChevronRight
} from 'components/blocks-components';
import { useTranslations } from 'next-intl';
import styles from './frequently-mobile-bought-together-drawer.module.scss';
import { ROOT_PORTALS } from 'constants/targets';
import { useFbtMobileDrawer } from './fbt-mobile-drawer-context';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import { PAGE_BREAKPOINTS } from 'constants/common';
import { FBT_LIST_NAME } from '../../../constants/constants';
import { useFbtContext } from '../fbt-context/fbt-context-provider';
import { getItemByPartNumber } from '../utils';
import { AllFbtItems, CurrentItems } from 'services/fbtItems/types';
import { ProductPriceAvailabilityHeadless } from 'services/priceAvailability/types';

export interface FBTMobileProps {
  children: React.ReactNode;
  storeLocationId: string;
  currentProduct: CurrentItems;
  fbtProducts: AllFbtItems[];
  fbtProductsSlice: AllFbtItems[];
  isCatCorp: boolean;
}
export const FBTMobileDrawer = ({
  children,
  storeLocationId,
  currentProduct,
  fbtProducts,
  fbtProductsSlice,
  isCatCorp
}: Readonly<FBTMobileProps>) => {
  const isGAEventTriggered = useRef(false);
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { drawerProps, openDrawer, closeDrawer, isDrawerOpen } =
    useFbtMobileDrawer();
  const t = useTranslations();
  const { cartDetails, currency, fbtProductsWithPrice, itemSummary } =
    useFbtContext();
  const cartId = cartDetails?.orderHeader?.orderId || null;

  const getFbtViewItemList = (currency: string) => ({
    item_list_id: FBT_LIST_NAME,
    item_list_name: FBT_LIST_NAME,
    currency: currency
  });

  const getUpdatedCurrentItem = (
    currentItem: CurrentItems,
    currentItemPriceData: ProductPriceAvailabilityHeadless
  ) => {
    return {
      ...currentItem,
      unformattedUnitPrice:
        currentItemPriceData?.price?.unformatted?.totalPrice,
      price: currentItemPriceData?.price?.formatted?.totalPrice
    };
  };

  const mergeFbtItems = useCallback(
    (products: AllFbtItems[]) => {
      if (isCatCorp || !products || !itemSummary?.products) {
        return [];
      }
      return products.map((item) => {
        const currentItemWithPrice = getItemByPartNumber(
          item.partNumber,
          fbtProductsWithPrice
        );
        const summaryItem = getItemByPartNumber(
          item.partNumber,
          itemSummary?.products
        );
        return {
          ...item,
          currentItemWithPrice,
          ...summaryItem,
          manufacturer: summaryItem?.manufacturer || 'Cat',
          unformattedUnitPrice:
            currentItemWithPrice?.price?.unformatted?.totalPrice,
          price: currentItemWithPrice?.price?.formatted?.totalPrice
        };
      });
    },
    [itemSummary, isCatCorp, fbtProductsWithPrice]
  );

  const fireGAEvent = useCallback(
    (forViewAllParts = false) => {
      const fbtViewItemList = getFbtViewItemList(currency);
      const currentItemWithPrice = getItemByPartNumber(
        currentProduct.partNumber,
        fbtProductsWithPrice
      );
      const updatedCurrentItem = getUpdatedCurrentItem(
        currentProduct,
        currentItemWithPrice
      );

      const selectedProducts = [
        updatedCurrentItem,
        ...mergeFbtItems(forViewAllParts ? fbtProducts : fbtProductsSlice)
      ];

      fireViewItemListEvent({
        itemListData: fbtViewItemList,
        locationId: storeLocationId,
        products: selectedProducts,
        cartId: cartId,
        cartItemsData: cartDetails,
        isProductsData: true
      });
    },
    [
      fireViewItemListEvent,
      currentProduct,
      storeLocationId,
      fbtProductsSlice,
      cartId,
      cartDetails,
      currency,
      fbtProductsWithPrice,
      fbtProducts,
      mergeFbtItems
    ]
  );

  useEffect(() => {
    if (
      isMD === false &&
      !isGAEventTriggered.current &&
      cartId &&
      currentProduct
    ) {
      fireGAEvent(false);
      isGAEventTriggered.current = true;
    }
  }, [fireGAEvent, cartId, isMD, currentProduct]);

  const onViewAllPartsPress = () => {
    openDrawer();
    fireGAEvent(true);
  };

  return (
    <>
      <CatButton
        className="align-self-end"
        data-testid="cat-button"
        onClick={onViewAllPartsPress}
      >
        {t('VIEW_ALL_PARTS')}
        <CatIconChevronRight slot="after" />
      </CatButton>

      <Portal id={ROOT_PORTALS}>
        <Drawer
          data-testId="fbt-drawer"
          className={styles['fbt-drawer__header']}
          orientation="side"
          hasBackdrop={false}
          onBlDrawerClose={closeDrawer}
          isActive={isDrawerOpen}
          {...drawerProps}
        >
          <DrawerHeader>
            <CatHeading
              className="text-capitalize"
              variant="title-sm"
              data-testId="fbt-heading"
            >
              {t('FREQUENTLY_BOUGHT_TOGETHER')}
            </CatHeading>
          </DrawerHeader>
          <DrawerBody className={styles['fbt_body_container']}>
            {children}
          </DrawerBody>
          <div slot="footer">
            <div id="fbt-mobile-drawer-footer-container" />
          </div>
        </Drawer>
      </Portal>
    </>
  );
};
